import React, { memo } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Rectangle,
  XAxis,
  YAxis,
} from 'recharts';

import { formatPercentage } from 'src/utils/format';
import {
  GENDER,
  GENDER_TONE,
  getGenderHistogramTone,
  getGenderLabel,
  getHistogramCategoryValue,
} from 'src/data/demographics';
import { COLOR_PRIMARY, COLORS } from 'src/data/ui';
import { useStyles } from './useStyles';

const formatXTick = (value) => formatPercentage(value, '0');
const formatYTick = (value) => {
  return getGenderLabel(value);
};

const RoundedBar = (props) => (
  <Rectangle
    {...props}
    radius={2}
  />
);

export const HistogramGender = memo(({ className, data, hideGrid, displayScoreTicks, margin }) => {
  const styles = useStyles();

  const femaleValue = getHistogramCategoryValue(data, GENDER.female);
  const maleValue = getHistogramCategoryValue(data, GENDER.male);
  const genderTone = getGenderHistogramTone(data);

  const highlightFemale = [GENDER_TONE.feminine, GENDER_TONE.neutral].includes(genderTone);
  const highlightMale = [GENDER_TONE.masculine, GENDER_TONE.neutral].includes(genderTone);

  const hasData = Array.isArray(data) && data.length > 0;
  const xTicks = displayScoreTicks
    ? [femaleValue, maleValue]
    : [0, Math.max(femaleValue, maleValue)];

  return (
    <ResponsiveContainer
      className={cx(styles.root, className, {
        [styles.highlightFemale]: highlightFemale,
        [styles.highlightMale]: highlightMale,
      })}
    >
      <BarChart
        data={data}
        isAnimationActive={false}
        layout="vertical"
        margin={margin}
      >
        {!hideGrid && (
          <CartesianGrid
            horizontal={false}
            vertical
            stroke={COLORS.primary.light}
            strokeDasharray="5 5"
          />
        )}

        <XAxis
          type="number"
          domain={[0, 100]}
          ticks={xTicks}
          hide={!hasData}
          tickFormatter={formatXTick}
          tickMargin={0}
          minTickGap={0}
          interval="preserveStartEnd"
          tickLine={false}
          axisLine={false}
        />

        <YAxis
          dataKey="name"
          type="category"
          ticks={hasData ? undefined : ['']}
          tickLine={false}
          axisLine={false}
          tickFormatter={formatYTick}
          orientation="right"
          width={42}
          padding={{ top: 0, bottom: 0 }}
        />

        <Bar
          dataKey="value"
          fill={COLOR_PRIMARY}
          isAnimationActive={false}
          shape={<RoundedBar />}
          barSize={16}
          background={{ fill: COLORS.neutral.silver }}
        />
      </BarChart>
    </ResponsiveContainer>
  );
});

HistogramGender.propTypes = {
  className: PropTypes.string,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.number,
    })
  ),
  hideGrid: PropTypes.bool,
  displayScoreTicks: PropTypes.bool,
  margin: PropTypes.shape({
    width: PropTypes.number,
    height: PropTypes.number,
  }),
};

HistogramGender.defaultProps = {
  className: null,
  data: [],
  hideGrid: false,
  displayScoreTicks: false,
  margin: { left: 10, right: 10 },
};
